import React, { useState, useEffect, useRef } from 'react';
import { useAlert } from '../components/ConfirmModal';
import { trackStoryFavorited, trackPdfExported, trackLibraryFiltered } from '../utils/analytics';

const LANG_FLAGS = { en:'🇬🇧', es:'🇪🇸', fr:'🇫🇷', hi:'🇮🇳', gu:'🇮🇳', de:'🇩🇪', pt:'🇧🇷', ar:'🇸🇦', zh:'🇨🇳', ja:'🇯🇵', it:'🇮🇹' };
const LANG_LABELS = { en:'English', es:'Spanish', fr:'French', hi:'Hindi', gu:'Gujarati', de:'German', pt:'Portuguese', ar:'Arabic', zh:'Chinese', ja:'Japanese', it:'Italian' };
import { Link } from 'react-router-dom';
import DashboardLayout from '../components/shared/DashboardLayout';
import { useAuth } from '../context/AuthContext';
import { entitledPlan } from '../utils/entitlements';
import API from '../utils/api';
import { saveOrShareFile } from '../utils/saveFile';

const GENRE_COLORS = {
  adventure: '#e67e22', magic: '#9b59b6', animals: '#27ae60', space: '#2980b9',
  ocean: '#1abc9c', funny: '#f39c12', mystery: '#7f8c8d', friendship: '#f1c40f',
  educational: '#3498db', cozy: '#e74c3c',
};

const StoryLibraryPage = () => {
  const alert = useAlert();
  const [stories, setStories] = useState([]);
  const [children, setChildren] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [filter, setFilter] = useState({ childId: '', favorite: '', language: '' });
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [exportingId, setExportingId] = useState(null);
  const [viewMode, setViewMode] = useState('grid'); // 'grid' | 'bookshelf'
  const { user } = useAuth();
  const hasPaidPlan = entitledPlan(user) !== 'free';

  const handleExportPdf = async (e, story) => {
    e.preventDefault();
    setExportingId(story._id);
    try {
      const res = await API.get(`/stories/${story._id}/export/pdf`, { responseType: 'blob' });
      const filename = `${story.title.replace(/[^a-z0-9 ]/gi, '').replace(/\s+/g, '_') || 'story'}.pdf`;
      // Shared with StoryReaderPage: inside the app a blob <a download> click
      // silently does nothing, so native writes the file and opens the share
      // sheet instead. Keeping one implementation is what stops the two paths
      // drifting again — the reader's copy never got this and was dead in-app.
      const how = await saveOrShareFile(res.data, filename, { title: story.title, dialogTitle: 'Save Story PDF' });
      if (how === 'cancelled') return;
      trackPdfExported();
    } catch (err) {
      await alert({ title: 'Export failed', message: err.response?.data?.message || 'Failed to export PDF.', danger: true });
    } finally {
      setExportingId(null);
    }
  };

  // .catch: every other call on this page handles failure; without it a 4xx here
  // is an unhandled rejection and the child filter silently stays empty.
  useEffect(() => {
    API.get('/children')
      .then(res => setChildren(res.data.children || []))
      .catch(() => setChildren([]));
  }, []);

  // Guards against an out-of-order response. Clicking between filters faster
  // than a round-trip (All → Favourites → All on a slow connection) could let
  // the FIRST request resolve last and repaint the list with the wrong set,
  // while the filter buttons showed the newer selection. Only the most recent
  // request is allowed to write state.
  const requestIdRef = useRef(0);

  const fetchStories = () => {
    const requestId = ++requestIdRef.current;
    const isStale = () => requestId !== requestIdRef.current;

    setLoading(true);
    const params = new URLSearchParams({ page, limit: 12, ...filter });
    setLoadError('');
    API.get(`/stories?${params}`).then(res => {
      if (isStale()) return;
      setStories(res.data.stories);
      setTotalPages(res.data.pages);
    }).catch(err => {
      if (isStale()) return;
      // Without this, a failed load fell through to the "No stories found" empty
      // state and told users with a full library that they had none.
      setLoadError(err.response?.data?.message || 'Could not load your stories. Please try again.');
    }).finally(() => { if (!isStale()) setLoading(false); });
  };

  useEffect(() => { fetchStories(); }, [filter, page]);

  const toggleFavorite = async (e, storyId) => {
    e.preventDefault();
    const res = await API.put(`/stories/${storyId}/favorite`);
    setStories(prev => prev.map(s => s._id === storyId ? { ...s, isFavorite: res.data.isFavorite } : s));
    if (res.data.isFavorite) trackStoryFavorited();
  };

  return (
    <DashboardLayout title="Story Library">
      {/* Library / Series tabs */}
      <div className="d-flex gap-2 mb-4">
        <span className="btn btn-dream rounded-pill px-4 fw-semibold" style={{ fontSize: '14px', cursor: 'default' }}>
          <i className="bi bi-book me-2"></i>Library
        </span>
        <Link to="/dashboard/series" className="btn btn-outline-secondary rounded-pill px-4" style={{ fontSize: '14px' }}>
          <i className="bi bi-collection-play me-2"></i>Story Series
        </Link>
      </div>

      {/* Filters */}
      <div className="card border-0 shadow-sm mb-4 p-3" style={{borderRadius:'16px'}}>
        <div className="d-flex flex-wrap gap-3 align-items-center">
          <select className="form-select w-auto" value={filter.childId}
            onChange={e => { setFilter(f => ({...f, childId:e.target.value})); setPage(1); trackLibraryFiltered('child', e.target.value); }}>
            <option value="">All Children</option>
            {children.map(c => <option key={c._id} value={c._id}>{c.avatar} {c.name}</option>)}
          </select>
          <div className="d-flex gap-2">
            <button onClick={() => { setFilter(f => ({...f, favorite:''})); setPage(1); trackLibraryFiltered('favorite', 'all'); }}
              className={`btn btn-sm rounded-pill ${filter.favorite === '' ? 'btn-dream' : 'btn-outline-secondary'}`}>All</button>
            <button onClick={() => { setFilter(f => ({...f, favorite:'true'})); setPage(1); trackLibraryFiltered('favorite', 'true'); }}
              className={`btn btn-sm rounded-pill ${filter.favorite === 'true' ? 'btn-danger' : 'btn-outline-danger'}`}>
              <i className="bi bi-heart-fill me-1"></i>Favourites
            </button>
          </div>
          <select className="form-select w-auto" value={filter.language}
            onChange={e => { setFilter(f => ({...f, language: e.target.value})); setPage(1); trackLibraryFiltered('language', e.target.value); }}>
            <option value="">All Languages</option>
            {Object.entries(LANG_LABELS).map(([code, label]) => (
              <option key={code} value={code}>{LANG_FLAGS[code]} {label}</option>
            ))}
          </select>
          <span className="text-muted small ms-auto">{stories.length} stories shown</span>
          <div className="btn-group btn-group-sm ms-2" role="group">
            <button
              className={`btn ${viewMode === 'grid' ? 'btn-dream' : 'btn-outline-secondary'} rounded-pill-start`}
              onClick={() => setViewMode('grid')} title="Grid view">
              <i className="bi bi-grid"></i>
            </button>
            <button
              className={`btn ${viewMode === 'bookshelf' ? 'btn-dream' : 'btn-outline-secondary'} rounded-pill-end`}
              onClick={() => setViewMode('bookshelf')} title="Bookshelf view">
              <i className="bi bi-journals"></i>
            </button>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="text-center py-5"><div className="spinner-border" style={{color:'#6f42c1'}}></div></div>
      ) : loadError ? (
        <div className="text-center py-5">
          <div className="fs-1 mb-3">⚠️</div>
          <h5 className="fw-bold" style={{color:'var(--text-main)'}}>Couldn't load your library</h5>
          <p className="text-muted mb-4">{loadError}</p>
          <button className="btn btn-dream rounded-pill px-5" onClick={fetchStories}>Try Again</button>
        </div>
      ) : stories.length === 0 ? (
        <div className="text-center py-5">
          <div className="fs-1 mb-3">📚</div>
          <h5 className="fw-bold" style={{color:'var(--text-main)'}}>No stories found</h5>
          <p className="text-muted mb-4">{filter.favorite === 'true' ? 'No favourited stories yet.' : 'Generate your first story to get started.'}</p>
          <Link to="/dashboard/generate" className="btn btn-dream rounded-pill px-5">Generate a Story</Link>
        </div>
      ) : (
        <>
          {/* ── Bookshelf view ── */}
          {viewMode === 'bookshelf' && (
            <div className="mb-4">
              <div className="p-4 rounded-4 mb-3" style={{ background: 'linear-gradient(180deg, #c8a96e 0%, #a07850 100%)', boxShadow: '0 8px 24px rgba(0,0,0,0.3)' }}>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', alignItems: 'flex-end', minHeight: '120px' }}>
                  {stories.map((story, i) => {
                    const spineColor = GENRE_COLORS[story.genre] || ['#6f42c1','#2980b9','#27ae60','#e67e22','#e74c3c'][i % 5];
                    const spineWidth = 28 + (story.readingTime || 5) * 2;
                    return (
                      <Link key={story._id} to={`/dashboard/story/${story._id}`}
                        className="text-decoration-none d-flex flex-column align-items-center"
                        title={story.title}
                        style={{ flexShrink: 0 }}>
                        <div style={{
                          width: `${spineWidth}px`,
                          height: `${90 + (story.readingTime || 5) * 3}px`,
                          background: spineColor,
                          borderRadius: '2px 3px 3px 2px',
                          boxShadow: '2px 2px 8px rgba(0,0,0,0.4), inset -2px 0 4px rgba(0,0,0,0.2)',
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          writingMode: 'vertical-rl', textOrientation: 'mixed',
                          color: 'rgba(255,255,255,0.9)', fontSize: '10px', fontWeight: 600,
                          padding: '6px 4px',
                          overflow: 'hidden',
                          transition: 'transform 0.15s, box-shadow 0.15s',
                          cursor: 'pointer',
                          position: 'relative',
                        }}
                          onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-8px) scale(1.03)'; e.currentTarget.style.boxShadow = '4px 4px 16px rgba(0,0,0,0.5), inset -2px 0 4px rgba(0,0,0,0.2)'; }}
                          onMouseLeave={e => { e.currentTarget.style.transform = ''; e.currentTarget.style.boxShadow = '2px 2px 8px rgba(0,0,0,0.4), inset -2px 0 4px rgba(0,0,0,0.2)'; }}>
                          {story.isFavorite && <span style={{ position: 'absolute', top: 4, fontSize: '10px' }}>❤️</span>}
                          <span style={{ textAlign: 'center', lineHeight: 1.2, marginTop: story.isFavorite ? '14px' : 0 }}>
                            {story.title.slice(0, 20)}{story.title.length > 20 ? '…' : ''}
                          </span>
                        </div>
                        <div style={{ fontSize: '9px', color: 'rgba(255,255,255,0.7)', marginTop: '4px', maxWidth: `${spineWidth}px`, textAlign: 'center', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {story.child?.name || ''}
                        </div>
                      </Link>
                    );
                  })}
                </div>
              </div>
              {/* Shelf plank */}
              <div style={{ height: '14px', background: 'linear-gradient(180deg,#8b6040,#6b4020)', borderRadius: '0 0 6px 6px', boxShadow: '0 4px 8px rgba(0,0,0,0.3)', marginTop: '-4px' }}></div>
              <p className="text-muted small text-center mt-3">{stories.length} books on your shelf · Hover to peek · Click to read</p>
            </div>
          )}

          {/* ── Grid view ── */}
          {viewMode === 'grid' && <div className="row g-4">
            {stories.map(story => (
              <div className="col-md-6 col-lg-4" key={story._id}>
                <Link to={`/dashboard/story/${story._id}`} className="text-decoration-none">
                  <div className="card border-0 shadow-sm h-100 card-hover" style={{borderRadius:'16px', overflow:'hidden'}}>
                    {story.illustrationUrl
                      ? <img src={story.illustrationUrl} alt={story.title}
                          style={{width:'100%', height:'160px', objectFit:'cover', display:'block'}} />
                      : <div className="d-flex align-items-center justify-content-center"
                          style={{height:'80px', background:'linear-gradient(135deg,#667eea,#764ba2)'}}>
                          {story.illustrationStatus === 'generating'
                            ? <span className="spinner-border spinner-border-sm text-white"></span>
                            : <span className="fs-2">🌙</span>}
                        </div>
                    }
                    <div className="card-body p-4">
                      <div className="d-flex align-items-start justify-content-between mb-2">
                        <div className="d-flex align-items-center gap-2">
                          {story.isMultiChild && story.multiChildIds?.length > 0 ? (
                            <>
                              <span className="fs-5" style={{letterSpacing:'-4px', paddingRight:'4px'}}>
                                {story.multiChildIds.map(c => c.avatar).join('')}
                              </span>
                              <span className="text-muted small">{story.multiChildIds.map(c => c.name).join(' & ')}</span>
                            </>
                          ) : (
                            <>
                              <span className="fs-4">{story.child?.avatar || '🧒'}</span>
                              <span className="text-muted small">{story.child?.name}</span>
                            </>
                          )}
                        </div>
                        <button className="btn btn-link p-0" onClick={e => toggleFavorite(e, story._id)}>
                          <i className={`bi ${story.isFavorite ? 'bi-heart-fill text-danger' : 'bi-heart text-muted'}`}></i>
                        </button>
                      </div>
                      <h6 className="fw-bold mb-2" style={{color:'var(--text-main)', lineHeight:'1.4'}}>{story.title}</h6>
                      <p className="text-muted small mb-3" style={{display:'-webkit-box', WebkitLineClamp:2, WebkitBoxOrient:'vertical', overflow:'hidden'}}>
                        {story.content?.substring(0, 100)}...
                      </p>
                      <div className="d-flex flex-wrap gap-1 mb-3">
                        {story.tags?.slice(0,3).map(tag => (
                          <span key={tag} className="badge rounded-pill small" style={{background:'#e8d5ff', color:'#6f42c1', fontSize:'10px'}}>{tag}</span>
                        ))}
                        {story.language && story.language !== 'en' && (
                          <span className="badge rounded-pill small" style={{background:'var(--badge-yellow-bg)', color:'var(--badge-yellow-fg)', fontSize:'10px'}}>
                            {LANG_FLAGS[story.language] || '🌐'} {LANG_LABELS[story.language] || story.language}
                          </span>
                        )}
                        {story.isMultiChild && (
                          <span className="badge rounded-pill small" style={{background:'#e8d5ff', color:'#6f42c1', fontSize:'10px'}}>
                            👨‍👩‍👧‍👦 Family Story
                          </span>
                        )}
                        {story.learningTopic && (
                          <span className="badge rounded-pill small" style={{background:'#dcfce7', color:'#15803d', fontSize:'10px'}}>
                            🎓 {story.learningTopic}
                          </span>
                        )}
                      </div>
                      <div className="d-flex align-items-center justify-content-between">
                        <span className="text-muted" style={{fontSize:'11px'}}>
                          <i className="bi bi-calendar3 me-1"></i>{new Date(story.createdAt).toLocaleDateString()}
                        </span>
                        <span className="text-muted" style={{fontSize:'11px'}}>
                          <i className="bi bi-clock me-1"></i>{story.readingTime || 5} min
                          {story.readCount > 0 && <><i className="bi bi-eye ms-2 me-1"></i>{story.readCount}</>}
                        </span>
                      </div>
                      {story.isPublished && (
                        <div className="mt-2">
                          <span className="badge rounded-pill" style={{background:'#dcfce7', color:'#15803d', fontSize:'10px'}}>
                            <i className="bi bi-globe2 me-1"></i>Published to Community
                          </span>
                        </div>
                      )}
                      {hasPaidPlan && (
                        <button
                          className="btn btn-sm btn-outline-secondary rounded-pill w-100 mt-2"
                          style={{fontSize:'11px'}}
                          onClick={e => handleExportPdf(e, story)}
                          disabled={exportingId === story._id}
                        >
                          {exportingId === story._id
                            ? <><span className="spinner-border spinner-border-sm me-1"></span>Exporting...</>
                            : <><i className="bi bi-file-earmark-pdf me-1"></i>Export PDF</>}
                        </button>
                      )}
                    </div>
                  </div>
                </Link>
              </div>
            ))}
          </div>}

          {totalPages > 1 && (
            <div className="d-flex justify-content-center gap-2 mt-5">
              <button className="btn btn-outline-secondary rounded-pill" disabled={page === 1} onClick={() => setPage(p => p-1)}>
                <i className="bi bi-chevron-left"></i>
              </button>
              {[...Array(totalPages)].map((_, i) => (
                <button key={i} onClick={() => setPage(i+1)}
                  className={`btn rounded-pill ${page === i+1 ? 'btn-dream' : 'btn-outline-secondary'}`}>
                  {i+1}
                </button>
              ))}
              <button className="btn btn-outline-secondary rounded-pill" disabled={page === totalPages} onClick={() => setPage(p => p+1)}>
                <i className="bi bi-chevron-right"></i>
              </button>
            </div>
          )}
        </>
      )}
    </DashboardLayout>
  );
};

export default StoryLibraryPage;
